import PropTypes from "prop-types";
import { Fragment, useContext } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { htmlSanitize } from "app/utils/sanitize";
import AppGlobalsContext from "app/AppGlobalsContext";
import { getBrandDisplayName } from "app/reducers/partnerSelector";
import { formatAmountForDisplay } from "app/utils/utils";
import AmountContainer from "app/pages/.shared/AmountContainer";
import "./paragraphs.scss";

/**
 * Paragraphs support inline template tokens with the syntax `{type}` or `{type:value}`.
 *
 * Supported tokens (see the handlers built inside the component):
 * - `{brandName}`      → partner display name from Redux (`partner.brandDisplayName`), e.g. "Air France"
 * - `{brand}`          → brand code from AppGlobalsContext, e.g. "AF"
 * - `{marketingCode}`  → partnerCode from AppGlobalsContext, e.g. "AFFR"
 * - `{shop}`           → shop from AppGlobalsContext, e.g. "fr-FR"
 * - `{langue}`         → language part of the shop (`shop.slice(0, 2)`), e.g. "fr"
 * - `{currency:1234}`  → the value formatted as a price in the current shop currency
 *
 * A token whose type has no handler — or whose handler returns null/undefined — is left as-is.
 */
const TEMPLATE_TOKEN_SPLIT_PATTERN = /(\{[^{}]+\})/g;
const TEMPLATE_TOKEN_PATTERN = /^\{([^{}:]+)(?::([^{}]+))?\}$/;
const TEMPLATE_TOKEN_REPLACE_PATTERN = /\{([^{}:]+)(?::([^{}]+))?\}/g;

/**
 * Renders a paragraph into an array of strings and React elements: plain text is kept
 * as-is, each known token is replaced by its handler's result (which may be a component,
 * e.g. `{currency:1234}` → <AmountContainer />). Used for the non-HTML branch.
 */
const templateRender = (paragraph, handlers) => {
	if (typeof paragraph !== "string") {
		return paragraph;
	}

	const tokens = paragraph.split(TEMPLATE_TOKEN_SPLIT_PATTERN);

	return tokens.map((token, tokenIndex) => {
		const match = token.match(TEMPLATE_TOKEN_PATTERN);

		if (!match) {
			return token;
		}

		const [, type, value] = match;
		const handler = handlers[type];

		if (!handler) {
			return token;
		}

		return <Fragment key={tokenIndex}>{handler(value)}</Fragment>;
	});
};

/**
 * String-only variant of templateRender for the `dangerouslySetInnerHTML` branches,
 * where React elements cannot be injected: every handler result is coerced to a string.
 * Runs BEFORE htmlSanitize so injected values are sanitized too.
 */
const templateRenderToString = (paragraph, handlers) => {
	if (typeof paragraph !== "string") {
		return paragraph;
	}

	return paragraph.replace(TEMPLATE_TOKEN_REPLACE_PATTERN, (token, type, value) => {
		const handler = handlers[type];

		if (!handler) {
			return token;
		}

		const replacement = handler(value);

		if (replacement === null || replacement === undefined) {
			return token;
		}

		return String(replacement);
	});
};

const Paragraphs = ({ paragraphs = [], enableHTMLFormatting = false }) => {
	const { shop = "", brand, partnerCode } = useContext(AppGlobalsContext);
	const brandDisplayName = useSelector(getBrandDisplayName);
	const currencyCode = useSelector(state => state.currency);
	const intl = useIntl();

	const textHandlers = {
		brandName: () => brandDisplayName,
		brand: () => brand,
		marketingCode: () => partnerCode,
		langue: () => shop.slice(0, 2),
		shop: () => shop,
	};

	const handlers = {
		...textHandlers,
		currency: value => <AmountContainer amount={Number(value)} />,
	};

	const htmlHandlers = {
		...textHandlers,
		currency: value => {
			const amount = Number(value);

			if (isNaN(amount)) {
				return null;
			}

			const money = intl.formatNumber(amount, {
				style: "currency",
				currency: currencyCode,
				currencyDisplay: "symbol",
				useGrouping: true,
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			});

			return formatAmountForDisplay(money, currencyCode, shop);
		},
	};

	if (Array.isArray(paragraphs)) {
		const paragraphNodes = paragraphs.map((paragraph, index) => {
			if (enableHTMLFormatting === true) {
				return (
					<article
						className="paragraphs__item"
						key={index}
						dangerouslySetInnerHTML={{
							__html: htmlSanitize(templateRenderToString(paragraph, htmlHandlers)),
						}}
					/>
				);
			}
			return (
				<article className="paragraphs__item" key={index}>
					{templateRender(paragraph, handlers)}
				</article>
			);
		});
		return <section className="paragraphs">{paragraphNodes}</section>;
	}
	return (
		<section
			className="paragraphs"
			dangerouslySetInnerHTML={{
				__html: htmlSanitize(templateRenderToString(paragraphs, htmlHandlers)),
			}}
		/>
	);
};

Paragraphs.propTypes = {
	paragraphs: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
	enableHTMLFormatting: PropTypes.bool,
};

export default Paragraphs;
